// ---------------------------------------------------------------------------
// Regra única da foto da pessoa logada (barra de cima, menu ☰, perfil).
// 1) Foto da ficha: vw_meu_perfil.foto_url (bucket privado 'fichas' → URL
//    assinada, como a tela de perfil já faz).
// 2) Sem foto da ficha: foto da conta Google (user_metadata.avatar_url/picture).
// 3) Sem nenhuma: iniciais do nome.
// Guarda a URL assinada em memória e renova perto de expirar, para não piscar a
// cada tela. Enquanto carrega, mostra as iniciais. Não altera nada no banco.
// ---------------------------------------------------------------------------
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import { urlFotoFicha } from '@/lib/foto';
import { iniciaisNome } from '@/pages/loja/utils';

const VALIDADE_S = 3600; // mesma validade que urlFotoFicha usa
const RENOVA_ANTES_MS = 5 * 60 * 1000; // renova 5 min antes de expirar

interface MinhaFotoCtx {
  /** Já sabemos o estado da foto (logado ou não)? */
  pronto: boolean;
  logado: boolean;
  /** Nome usado para as iniciais (perfil → metadata). */
  nome: string;
  /** URL pronta para exibir (foto da ficha assinada ou do Google). Null = sem foto. */
  fotoUrl: string | null;
  /** Iniciais do nome (vazio se não tiver nome). */
  iniciais: string;
  /** Atualiza a foto na hora (ao trocar no perfil). Passa o caminho salvo. */
  definirFoto: (caminho: string | null) => void;
  /** Relê a foto do banco (ex.: ao abrir o perfil). */
  recarregar: () => void;
}

const Contexto = createContext<MinhaFotoCtx>({
  pronto: false,
  logado: false,
  nome: '',
  fotoUrl: null,
  iniciais: '',
  definirFoto: () => {},
  recarregar: () => {},
});

export function useMinhaFoto(): MinhaFotoCtx {
  return useContext(Contexto);
}

interface DadosFoto {
  logado: boolean;
  nome: string;
  iniciais: string;
  /** Foto da conta Google, quando não há foto na ficha. */
  avatarGoogle: string | null;
  /** Caminho da foto da ficha (bucket 'fichas'). */
  caminho: string | null;
}

const VAZIO: DadosFoto = {
  logado: false,
  nome: '',
  iniciais: '',
  avatarGoogle: null,
  caminho: null,
};

export function MinhaFotoProvider({ children }: { children: ReactNode }) {
  const [dados, setDados] = useState<DadosFoto>(VAZIO);
  const [pronto, setPronto] = useState(false);
  const [reload, setReload] = useState(0);
  const [urlAssinada, setUrlAssinada] = useState<string | null>(null);

  const recarregar = useCallback(() => setReload((r) => r + 1), []);

  // Carrega a foto da ficha + nome da pessoa logada (ou limpa, se sai).
  useEffect(() => {
    let ativo = true;

    async function carregar() {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user ?? null;

      if (!user) {
        if (ativo) {
          setDados(VAZIO);
          setUrlAssinada(null);
          setPronto(true);
        }
        return;
      }

      const avatarGoogle =
        (user.user_metadata?.avatar_url as string | undefined) ||
        (user.user_metadata?.picture as string | undefined) ||
        null;

      let nome = '';
      let caminho: string | null = null;
      try {
        const { data } = await supabase
          .from('vw_meu_perfil')
          .select('nome, foto_url')
          .maybeSingle();
        const linha = data as { nome?: string | null; foto_url?: string | null } | null;
        nome = linha?.nome ?? '';
        caminho = linha?.foto_url ?? null;
      } catch {
        /* segue com os dados da conta */
      }

      if (!nome) {
        nome =
          (user.user_metadata?.full_name as string | undefined) ||
          (user.user_metadata?.name as string | undefined) ||
          '';
      }

      if (!ativo) return;
      setDados({
        logado: true,
        nome,
        iniciais: nome ? iniciaisNome(nome) : '',
        avatarGoogle,
        caminho,
      });
      setPronto(true);
    }

    carregar();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      // Fora do callback: o supabase pede para não chamar o banco dentro dele.
      window.setTimeout(() => {
        if (ativo) void carregar();
      }, 0);
    });

    return () => {
      ativo = false;
      sub.subscription.unsubscribe();
    };
  }, [reload]);

  // Assina a foto da ficha e renova perto de expirar (sem piscar a cada tela).
  useEffect(() => {
    let ativo = true;
    let timer: number | null = null;

    async function renovar(caminho: string) {
      const url = await urlFotoFicha(caminho);
      if (!ativo) return;
      setUrlAssinada(url);
      timer = window.setTimeout(
        () => void renovar(caminho),
        VALIDADE_S * 1000 - RENOVA_ANTES_MS,
      );
    }

    if (dados.caminho) {
      // Limpa a URL antiga pra não exibir a foto anterior por um instante.
      setUrlAssinada(null);
      void renovar(dados.caminho);
    } else {
      setUrlAssinada(null);
    }

    return () => {
      ativo = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [dados.caminho]);

  const definirFoto = useCallback((caminho: string | null) => {
    setDados((d) => ({ ...d, caminho }));
  }, []);

  // Enquanto carrega a foto da ficha, mostra as iniciais: só usa a imagem
  // depois que a URL assinada chega. Sem foto na ficha, cai no Google → iniciais.
  const fotoUrl = dados.caminho ? urlAssinada : dados.avatarGoogle;

  const ctx = useMemo<MinhaFotoCtx>(
    () => ({
      pronto,
      logado: dados.logado,
      nome: dados.nome,
      fotoUrl,
      iniciais: dados.iniciais,
      definirFoto,
      recarregar,
    }),
    [pronto, dados.logado, dados.nome, dados.iniciais, fotoUrl, definirFoto, recarregar],
  );

  return <Contexto.Provider value={ctx}>{children}</Contexto.Provider>;
}